import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

/**
 * The arrow that points at the Promotions megaphone on the home map.
 *
 * It exists only to make a freshly-published poster or video discoverable - the
 * megaphone alone is a small unlabelled icon most customers never tap. So it
 * renders ONLY when there is actually something to see: if the promotions list
 * is empty (or the request fails) this component draws nothing at all, and the
 * map is exactly as it was.
 *
 * Whether it is currently the right moment to show it is NOT decided here. The
 * dashboard owns that - it already knows about full-screen overlays, the drawer,
 * pin mode and "has the customer opened promotions since the app came back", and
 * it mounts this component only when all of that says yes. Keeping the two apart
 * means the nudge can never outlive the button it points at.
 */
const PromoNudge: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const [hasPromo, setHasPromo] = useState(false);

  // Re-checked on every mount, which (see the dashboard) is once per return to
  // the foreground - so a promo published while the customer was away is picked
  // up without them reinstalling or force-quitting anything.
  const check = useCallback(async (signal: AbortSignal) => {
    try {
      const res = await fetch(`${API_URL}/promotions`, { signal });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      if (!signal.aborted) setHasPromo(Array.isArray(data) && data.length > 0);
    } catch {
      // Offline, backend down, or nothing published - all mean "draw nothing".
      // A nudge pointing at an empty Promotions page is worse than no nudge.
      if (!signal.aborted) setHasPromo(false);
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    check(ctrl.signal);
    return () => ctrl.abort();
  }, [check]);

  if (!hasPromo) return null;

  return (
    <motion.button
      type="button"
      onClick={onOpen}
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      aria-label="Click to view latest SafariCab promo"
      className="flex items-center gap-1.5 pl-1.5 pr-3 py-1.5 rounded-full bg-safaricab-gold shadow-2xl max-w-[62vw]"
    >
      {/* The arrow travels back towards the megaphone rather than just pulsing -
          movement with a direction is what reads as "over there", and the button
          it points at sits immediately to the left of this pill. */}
      <motion.span
        animate={{ x: [0, -5, 0] }}
        transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
        className="flex items-center justify-center shrink-0"
      >
        <ArrowLeft className="w-4 h-4 text-black" strokeWidth={3} />
      </motion.span>
      <span className="text-black text-[11px] font-bold leading-tight text-left">
        Click to view latest SafariCab promo
      </span>
    </motion.button>
  );
};

export default PromoNudge;
